import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import cogoToast from "cogo-toast";
import EditCard from "../EditCard";
import CustomButton from "../../../../../components/CustomButton";
import icons from "../../../../../assets/icons/icon-collection.svg";
import AddLink from "./AddLink";
import { uploadFile } from "../../../../../utils/helpers";
import { updateProfileAction } from "../../../../../redux/actions/profileActions";
import { parseResumeWithAI } from "../../../../../utils/aiResumeParser";
import { extractTextFromPDF, extractTextFromDOCX } from "../../../../../utils/resumeParser";

const Resume = () => {
  const { profileData } = useSelector((store) => store.profile);
  const [state, setState] = useState({
    resume: profileData?.resume,
    resumeName: profileData?.resumeName,
    facebook: profileData?.links ? profileData?.links[0]?.facebook : "",
    linkdn: profileData?.links ? profileData?.links[0]?.linkdn : "",
    twitter: profileData?.links ? profileData?.links[0]?.twitter : "",
    potfolio: profileData?.links ? profileData?.links[0]?.potfolio : "",
    resumePreview: "",
    resumeExtractedText: "",
    resumeExtractedSkills: [],
  });
  const dispatch = useDispatch();
  const history = useHistory();
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [showMatchScoreModal, setShowMatchScoreModal] = useState(false);

  const uploadInputRef = useRef(null);

  const handleRemove = ({ target: { name } }) => {
    if (name === "resume") {
      setState((prevState) => ({
        ...prevState,
        [name]: "",
        resumePreview: "",
        resumeExtractedText: "",
        resumeExtractedSkills: [],
      }));
    } else {
      setState((prevState) => ({ ...prevState, [name]: "" }));
    }
  };

  const handleChangeUpload = async ({ target: { files } }) => {
    if (files && files[0]) {
      const file = files[0];
      setIsUploading(true);

      try {
        const { data, success } = await uploadFile(
          file,
          "https://api.cloudinary.com/v1_1/workbrook-hash/raw/upload"
        );

        if (success) {
          let extractedText = "";
          let extractedSkills = [];

          try {
            const fileType = file.type;
            if (fileType === "application/pdf") {
              extractedText = await extractTextFromPDF(file);
            } else if (
              fileType ===
              "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            ) {
              extractedText = await extractTextFromDOCX(file);
            }

            const parsedData = await parseResumeWithAI(file);
            extractedSkills = parsedData.skills || [];
          } catch (parseErr) {
            console.warn("Resume parsing failed, saving without extraction:", parseErr);
          }

          setState((prevState) => ({
            ...prevState,
            resumePreview: data,
            resumeName: file.name,
            resumeExtractedText: extractedText,
            resumeExtractedSkills: extractedSkills,
          }));
        } else {
          cogoToast.error("Unsuccessful file upload", {
            hideAfter: 4,
            position: "top-center",
          });
        }
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleSave = () => {
    setIsSaving(true);

    // Build links only if at least one valid URL is provided
    const urlLike = (val) => !!val && /^(https?:)?\/\//i.test(val);
    const anyLinkProvided =
      urlLike(state.facebook) ||
      urlLike(state.linkdn) ||
      urlLike(state.twitter) ||
      urlLike(state.potfolio);

    const basePayload = {
      first_name: profileData.first_name || "",
      last_name: profileData.last_name || "",
      gender: profileData.gender || "",
      dob: profileData.dob || "",
      state: profileData.state || "",
      country: profileData.country || "",
      industry: profileData.industry || [],
      image_url: profileData.image_url || "",
      resume: (state.resumePreview || state.resume || "").toString(),
      phone: profileData.phone || "",
      isShowOnProfile: 1,
      about: profileData.about || "",
    };

    if (basePayload.resume) {
      basePayload.resumeName = state.resumeName || "";
      if (state.resumeExtractedText) {
        basePayload.resume_extracted_text = state.resumeExtractedText;
      }
      if (state.resumeExtractedSkills?.length) {
        basePayload.resume_extracted_skills = state.resumeExtractedSkills;
      }
    }

    if (anyLinkProvided) {
      basePayload.links = [
        {
          facebook: urlLike(state.facebook) ? state.facebook : "",
          linkdn: urlLike(state.linkdn) ? state.linkdn : "",
          twitter: urlLike(state.twitter) ? state.twitter : "",
          potfolio: urlLike(state.potfolio) ? state.potfolio : "",
        },
      ];
    }

    Promise.resolve(updateProfileAction(basePayload, dispatch))
      .then(() => {
        cogoToast.success("Resume and links updated successfully!", {
          hideAfter: 4,
          position: "top-center",
        });
        // Show Job Match Score prompt after successful save
        setShowMatchScoreModal(true);
      })
      .catch((error) => {
        console.error("Error updating resume:", error);
        cogoToast.error(error?.message || "Failed to update resume and links", {
          hideAfter: 4,
          position: "top-center",
        });
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const handleRefClick = () => {
    uploadInputRef.current.click();
  };

  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      resume: profileData.resume || prevState.resume,
      resumeName: profileData.resumeName || prevState.resumeName,
      facebook: profileData.links ? profileData?.links[0]?.facebook : "",
      linkdn: profileData.links ? profileData?.links[0]?.linkdn : "",
      twitter: profileData.links ? profileData?.links[0]?.twitter : "",
      potfolio: profileData.links ? profileData?.links[0]?.potfolio : "",
    }));
  }, [open, profileData]);

  return (
    <EditCard
      loading={isSaving}
      handleBtnClick={handleSave}
      btnType="button"
      disable={isUploading}
      allRequired={false}
    >
      <div className="w-100">
        <h6>Resume/Attached Documents</h6>
        <p>
          Add Files or links to external documents, photos, sites, videos and
          presentations.{" "}
        </p>
        <p
          className="co-lightred medium-text mb-1 d-block"
          style={{ paddingBottom: "10px" }}
        >
          Resume should be in pdf or doc format.
        </p>
        <div className="my-4 d-flex flex-column">
          <div className="d-flex mb-3">
            <div className="w-50 mr-1">
              <input
                ref={uploadInputRef}
                type="file"
                onChange={handleChangeUpload}
                style={{ display: "none" }}
                accept="application/pdf, .docx"
              />

              <CustomButton
                backgroundColor="#3A6ED4"
                color="#fff"
                text="Upload Resume"
                additionalClass="rounded"
                handleClick={handleRefClick}
                loading={isUploading}
              />
            </div>
            <div className="w-50 ml-1">
              <CustomButton
                type="button"
                handleClick={() => setOpen(true)}
                backgroundColor="#fff"
                color="#3A6ED4"
                text="Add Links"
                additionalClass="rounded border-primary"
                disabled={isUploading}
              />
            </div>
          </div>

          <AddLink open={open} handleClose={() => setOpen(false)} />
        </div>

        {/* <div className="link-count my-5 w-100 d-flex justify-content-end">
          <span className="blob rounded px-2 py-1 bg-green co-white"><b>4/20</b></span>
        </div> */}

        <div className="d-flex flex-column mt-4">
          {state.resumeName && (
            <span className="d-flex justify-content-between my-2">
              <a
                className="d-flex"
                href={state.resumePreview || state.resume}
                target="_blank"
                rel="noopener noreferrer"
                download
              >
                <svg width="20" height="24" className="mr-2">
                  <use href={`${icons}#file`}></use>
                </svg>
                {state.resumeName}
                <span className="flag mx-3 co-primary small-text px-1">
                  Default
                </span>
              </a>
              <button
                name="resume"
                onClick={handleRemove}
                className="px-1 delete rounded-circle bg-pink"
              >
                <svg width="10" height="8" style={{ pointerEvents: "none" }}>
                  <use href={`${icons}#close-blue`}></use>
                </svg>
              </button>
            </span>
          )}

          {/* <span className="d-flex justify-content-between my-2">
            <div className="d-flex">
              <svg width="20" height="24" className="mr-2">
                <use href={`${icons}#file`}></use>
              </svg>
              Jane_Doe_Cover_Letter.pdf
            </div>
            <button className="px-1 delete rounded-circle bg-pink">
              <svg width="10" height="8" >
                <use href={`${icons}#close-blue`}></use>
              </svg>
            </button>
          </span> */}
          {state?.facebook && (
            <span className="d-flex justify-content-between my-2">
              <div className="d-flex">
                <svg width="22" height="22" className="mr-2">
                  <use href={`${icons}#link`}></use>
                </svg>
                <a
                  className="underlined co-primary mr-2"
                  href={state.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Facebook
                </a>
              </div>
              <button
                name="facebook"
                onClick={handleRemove}
                className="px-1 delete rounded-circle bg-pink"
              >
                <svg width="10" height="8" style={{ pointerEvents: "none" }}>
                  <use href={`${icons}#close-blue`}></use>
                </svg>
              </button>
            </span>
          )}

          {state.potfolio && (
            <span className="d-flex justify-content-between my-2">
              <div className="d-flex">
                <svg width="22" height="22" className="mr-2">
                  <use href={`${icons}#link`}></use>
                </svg>
                <a
                  className="underlined co-primary mr-2"
                  href={state.potfolio}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Portfolio
                </a>
              </div>
              <button
                name="potfolio"
                onClick={handleRemove}
                className="px-1 delete rounded-circle bg-pink"
              >
                <svg width="10" height="8" style={{ pointerEvents: "none" }}>
                  <use href={`${icons}#close-blue`}></use>
                </svg>
              </button>
            </span>
          )}

          {state.twitter && (
            <span className="d-flex justify-content-between my-2">
              <div className="d-flex">
                <svg width="22" height="22" className="mr-2">
                  <use href={`${icons}#link`}></use>
                </svg>
                <a
                  className="underlined co-primary mr-2"
                  href={state.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Twitter
                </a>
              </div>
              <button
                name="twitter"
                onClick={handleRemove}
                className="px-1 delete rounded-circle bg-pink"
              >
                <svg width="10" height="8" style={{ pointerEvents: "none" }}>
                  <use href={`${icons}#close-blue`}></use>
                </svg>
              </button>
            </span>
          )}

          {state.linkdn && (
            <span className="d-flex justify-content-between my-2">
              <div className="d-flex">
                <svg width="22" height="22" className="mr-2">
                  <use href={`${icons}#link`}></use>
                </svg>
                <a
                  className="underlined co-primary mr-2"
                  href={state.linkdn}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  LinkedIn
                </a>
              </div>
              <button
                name="linkdn"
                onClick={handleRemove}
                className="px-1 delete rounded-circle bg-pink"
              >
                <svg width="10" height="8" style={{ pointerEvents: "none" }}>
                  <use href={`${icons}#close-blue`}></use>
                </svg>
              </button>
            </span>
          )}

          {/* {[
            !state.resume,
            !state.facebook,
            !state.linkdn,
            !state.twitter,
            !state.potfolio,
          ].every((val) => val === true) && (
            <p className="mr-2 py-1 px-2 co-darkgray">Nothing to display</p>
          )} */}
        </div>
      </div>

      {/* Job Match Score Prompt Modal */}
      {showMatchScoreModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
          onClick={() => setShowMatchScoreModal(false)}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "16px",
              padding: "32px 28px",
              maxWidth: "420px",
              width: "90%",
              textAlign: "center",
              boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Icon */}
            <div
              style={{
                width: "56px",
                height: "56px",
                background: "linear-gradient(135deg, #3A6ED4 0%, #1E93E2 100%)",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
              }}
            >
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <circle cx="12" cy="12" r="6" />
                <circle cx="12" cy="12" r="2" />
              </svg>
            </div>

            <h3
              style={{
                fontSize: "18px",
                fontWeight: "700",
                color: "#1E2329",
                margin: "0 0 8px",
              }}
            >
              Resume Updated!
            </h3>
            <p
              style={{
                fontSize: "14px",
                color: "#4A5568",
                lineHeight: "1.6",
                margin: "0 0 24px",
              }}
            >
              Your resume is now up to date. Run a <strong>Job Match Score</strong> scan
              to see how well you match against job postings with your updated profile.
            </p>

            <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
              <CustomButton
                additionalClass="p-3"
                text="Maybe Later"
                backgroundColor="#fff"
                color="#6B7785"
                border="1px solid #E8ECF0"
                handleClick={() => setShowMatchScoreModal(false)}
              />
              <CustomButton
                additionalClass="p-3"
                text="Run Match Score"
                backgroundColor="#3A6ED4"
                color="#fff"
                handleClick={() => {
                  setShowMatchScoreModal(false);
                  history.push("/job-match-score");
                }}
              />
            </div>
          </div>
        </div>
      )}
    </EditCard>
  );
};

export default Resume;
